import { useMemo } from 'react';
import { ApolloClient, InMemoryCache, ApolloLink, HttpLink, from } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { v4 as uuid } from 'uuid';
import { OperationDefinitionNode } from 'graphql';
import possibleTypes from '../../types/possibleTypes.json';
import { onError } from '@apollo/client/link/error';

const cleanTypenameLink = new ApolloLink((operation, forward) => {
  const omitTypename = (key: string, value: string): string | undefined =>
    key === '__typename' ? undefined : value;

  const def = getMainDefinition(operation.query);

  if (def && (def as OperationDefinitionNode).operation === 'mutation') {
    operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
  }

  return forward ? forward(operation) : null;
});

const addRequestIdLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers }: { headers: Record<string, string> }) => ({
    headers: {
      'Request-Id': uuid(),
      ...headers,
    },
  }));
  return forward(operation);
});

const errorLink = onError(({ networkError }) => {
  if (networkError && 'statusCode' in networkError) {
    if (networkError.statusCode === 401) {
      if (typeof window !== 'undefined') {
        window.location.replace(window.location.origin);
      }
    }
  }
});

let apolloClient: ApolloClient<unknown>;

function createApolloClient(
  apiConfig = { host: '', scheme: '', origin: '' },
  fetch?: unknown
): ApolloClient<unknown> {
  return new ApolloClient<unknown>({
    name: 'chop-admin-client',
    ssrMode: typeof window === 'undefined',
    cache: new InMemoryCache({
      possibleTypes,
    }),
    link: from([
      addRequestIdLink,
      cleanTypenameLink,
      errorLink,
      new HttpLink({
        uri: `${apiConfig.origin}/graphql`,
        credentials: 'include',
        headers: {
          'Application-Domain': apiConfig.host,
        },
        // @ts-expect-error - idk
        fetch,
      }),
    ]),
  });
}

export function initializeApollo(
  apiConfig = { host: '', scheme: '', origin: '' },
  initialState = null,
  fetch?: unknown
): ApolloClient<unknown> {
  const _apolloClient = apolloClient ?? createApolloClient(apiConfig, fetch);

  // Hydrate store with data fetched in pages
  if (initialState) {
    _apolloClient.cache.restore(initialState);
  }

  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return _apolloClient;

  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
}

export function useApollo(
  apiConfig = { host: '', scheme: '', origin: '' },
  initialState = null
): ApolloClient<unknown> {
  return useMemo(() => initializeApollo(apiConfig, initialState), [apiConfig, initialState]);
}
